import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { MachineDto, MachinePlacementDto, MachinePlacementItemDto, MachineRangeDto } from 'src/app/shared/dtos/machine.dto';
import { LOCAL_STORAGE_KEYS } from 'src/app/shared/constants/local-storage.constants';
import { ContentListResponseDto } from 'src/app/shared/dtos/content-list.dto';
import { ISelectOption } from 'src/app/shared/interfaces/primeng.interface';
import { Select, SelectChangeEvent } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { FormsModule } from '@angular/forms';
import { PlacementContent } from 'src/app/core/components/placement-content/placement-content';
import { CardModule } from 'primeng/card';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { AnalyticsEventName } from 'src/app/core/enums/analytics.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-machine',
  imports: [
    AsyncPipe,
    Select,
    FormsModule,
    RouterModule,
    TabsModule,
    PlacementContent,
    CardModule,
    TranslocoPipe,
  ],
  templateUrl: './machine.html',
  styleUrl: './machine.scss',
})
export class Machine implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly machineId$ = this.activatedRoute.params.pipe(
    map(params => params['machine-id']),
  );

  protected readonly contentList$: Observable<ContentListResponseDto> = this.activatedRoute.parent!.data.pipe(
    map(data => data['contentList']),
  );

  protected readonly machine$: Observable<MachineDto | undefined> = combineLatest([
    this.activatedRoute!.data,
    this.contentList$
  ]).pipe(
    map(([data, contentList]) => {
      const recentlyViewedItems = this.localStorageService.getItem<MachineDto[]>(LOCAL_STORAGE_KEYS.RecentlyViewedItems) ?? [];
      const machine = data['machine'] as MachineDto;

      const isMachineInContentList = contentList.content.some(group => {
        return machine && group.children.find(child => child.id === machine.id) !== undefined;
      })

      if (!machine || !isMachineInContentList) {
        void this.router.navigate(['/not-found']);
        return;
      }

      const existingIndex = recentlyViewedItems.findIndex(item => item.id === machine.id);

      if (existingIndex > -1 && machine) {
        recentlyViewedItems.splice(existingIndex, 1);
        recentlyViewedItems.unshift(machine);
        this.localStorageService.setItem(LOCAL_STORAGE_KEYS.RecentlyViewedItems, recentlyViewedItems);
      }

      if (recentlyViewedItems.length === 5) {
        recentlyViewedItems.shift();
      }

      if (existingIndex === -1 && machine) {
        recentlyViewedItems.unshift(machine);
        this.localStorageService.setItem(LOCAL_STORAGE_KEYS.RecentlyViewedItems, recentlyViewedItems);
      }

      machine.placements = machine.placements.filter(placement => placement.items.length > 0);

      return machine;
    }),
  );

  protected readonly owningRange$: Observable<MachineRangeDto | undefined> = combineLatest([this.machine$, this.contentList$]).pipe(
    map(([machine, contentlist]) => {
      return contentlist.content.find(group => {
        return machine && group.children.find(child => child.id === machine.id) !== undefined;
      });
    }),
  );

  protected readonly machinesSelectOptions$: Observable<ISelectOption[] | undefined> = this.owningRange$.pipe(
    map(machineRange => {
      return machineRange?.children.map(machine => {
        return {
          name: machine.title,
          code: machine.id,
        };
      });
    }),
  );

  protected readonly selectedMachine$ = new BehaviorSubject<ISelectOption | undefined>(undefined);

  protected readonly selectedPlacement$ = new BehaviorSubject<ISelectOption | undefined>(undefined);

  protected readonly placementCategoryOptions$: Observable<ISelectOption[] | undefined> = this.machine$.pipe(
    map(machine => {
      if (!machine) {
        return [];
      }

      const placementOptions = machine.placements.filter(machine => {
        return machine.items.length > 0;
      }).map(placement => {
        return {
          code: placement.id,
          name: placement.name,
        };
      });
      this.selectedPlacement$.next(placementOptions.at(0));
      return placementOptions;
    }),
  );

  protected selectedPlacementItems$: Observable<MachinePlacementItemDto[] | undefined> = combineLatest([
    this.machine$,
    this.selectedPlacement$,
  ]).pipe(
    map(([machine, selectedPlacement]) => {
      return machine?.placements.find(placement => placement.id === selectedPlacement?.code)?.items;
    }),
  );

  ngOnInit(): void {
    combineLatest([
      this.machinesSelectOptions$,
      this.machineId$,
      this.owningRange$,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(0),
      distinctUntilChanged((prev, curr) => prev[1] === curr[1]),
    ).subscribe(([machines, machineId, range]) => {
      if (!machines || !machineId || !range) {
        return;
      }

      const machineIdNum = parseInt(machineId);
      const selectedMachine = machines.find((machine: ISelectOption) => machine.code === machineIdNum);
      this.selectedMachine$.next(selectedMachine || { name: 'Select a machine', code: null });

      this.analyticsService.analyticsPageData = {
        itemId: machineIdNum,
        itemName: selectedMachine?.name || 'machine',
        jcbMachineId: machineIdNum,
        jcbMachineName: selectedMachine?.name || 'machine',
        jcbRangeId: range?.id || '',
        jcbRangeName: range?.name || 'range',
      };

      this.analyticsService.trackPageLoad();
    });
  }

  protected handleSpecCheckClick(): void {
    this.analyticsService.trackEvent(AnalyticsEventName.ItemClick, {
      ...this.analyticsService.analyticsPageData,
      itemName: 'speccheck',
      itemId: 'speccheck',
    });
  }

  protected handlePlacementTabClick(tab: MachinePlacementDto): void {
    this.analyticsService.trackEvent(AnalyticsEventName.ItemClick, {
      ...this.analyticsService.analyticsPageData,
      itemName: tab.name,
      itemId: tab.id,
    });
  }

  protected handlePlacementSelectChange(event: SelectChangeEvent): void {
    this.analyticsService.trackEvent(AnalyticsEventName.ItemClick, {
      ...this.analyticsService.analyticsPageData,
      itemName: event.value.name,
      itemId: event.value.code,
    });
  }

  protected handleChangePage(event: SelectChangeEvent, range: MachineRangeDto): void {
    void this.router.navigate(['machine', event.value.code]);

    this.analyticsService.analyticsPageData = {
      itemId: event.value.code,
      itemName: event.value.name || 'machine',
      jcbMachineId: event.value.code,
      jcbMachineName: event.value.name || 'machine',
      jcbRangeId: range?.id || '',
      jcbRangeName: range?.name || 'range',
    };

    this.analyticsService.trackPageLoad();
  }
}
